import {
  ChangeDetectionStrategy,
  Component,
  input,
  inject,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { TENANTS_INFO } from '@fitness-force/state';
import { AuthSevices, AdvancedSettingService } from '@fitness-force/api';
import { ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { DatePickerModule } from 'primeng/datepicker';
import { MultiSelectModule } from 'primeng/multiselect';
import { MessageModule } from 'primeng/message';
import { ErrorMsgComponent } from '@fitness-force/errors';
import type { InvoiceOccurance, TENANTSINTERFACE } from '@fitness-force/models';
import { FreezeDateService } from '../../services/freeze-date.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'freeze-date-selection',
  templateUrl: './freeze-date-selection.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    DatePickerModule,
    MultiSelectModule,
    MessageModule,
    ErrorMsgComponent,
  ],
})
export class FreezeDateSelectionComponent implements OnInit, OnDestroy {
  private dateService = inject(FreezeDateService);
  private authService = inject(AuthSevices);
  private advanceSettingService = inject(AdvancedSettingService);
  private store = inject(Store);
  private destroy$ = new Subject<void>();

  minStartDate: Date = new Date();
  minEndDate: Date = new Date();
  currentLoggedinUser: any;
  tenantId: number = 0;

  readonly formGroup = input.required<UntypedFormGroup>();
  readonly isRecurring = input<boolean>(false);
  readonly freezeCanOnlyBillingCycle = input<boolean>(false);
  readonly filteredTimeline = input<InvoiceOccurance[]>([]);
  readonly selectionLimit = input<number>(10);
  readonly freezeV2Flow = input<boolean>(false);

  get showDatePickers(): boolean {
    return !this.isRecurring() || !this.freezeCanOnlyBillingCycle();
  }

  get showMultiselect(): boolean {
    return this.isRecurring() && this.freezeCanOnlyBillingCycle();
  }

  get startDateControl() {
    return this.formGroup().controls['startdate'] ?? null;
  }

  get endDateControl() {
    return this.formGroup().controls['enddate'] ?? null;
  }

  get selectedFreezeAttemptControl() {
    return this.formGroup().controls['selectedfreezeattempt'] ?? null;
  }

  calculateDays(): number {
    const start = this.startDateControl?.value;
    const end = this.endDateControl?.value;
    return this.dateService.calculateFreezeDays(start, end);
  }

  ngOnInit() {
    this.currentLoggedinUser = this.authService.decodedToken;
    this.store.select(TENANTS_INFO).pipe(takeUntil(this.destroy$)).subscribe((tenant: TENANTSINTERFACE) => {
      this.tenantId = tenant.selected_tenant.id;
      this.getSettings();
    });

    this.startDateControl?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((startDate: Date | null) => {
      if (startDate) {
        this.minEndDate = new Date(startDate);

        const currentEndDate = this.endDateControl?.value;
        if (currentEndDate && currentEndDate < startDate) {
          this.endDateControl?.setValue(null);
        }
      } else {
        this.minEndDate = new Date(this.minStartDate);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getSettings() {
    this.minStartDate = new Date();
    this.minStartDate.setHours(0, 0, 0, 0);
    this.updateMinEndDate();

    if (this.tenantId > 0) {
      const settings = this.advanceSettingService.getAdvanceSettings(this.tenantId);
      if (settings) {
        settings.subscribe({
          next: (res: any) => {
            if (res.items != null) {
              const backdated_billing_setting: any = res.items.find((x: { key: string; }) => x.key.toLowerCase() == "backdated_billing_setting");
              if (backdated_billing_setting != null && backdated_billing_setting.value > 0) {
                this.minStartDate.setDate(this.minStartDate.getDate() - parseInt(backdated_billing_setting.value));
                this.updateMinEndDate();
              }
            }
          }
        });
      }
    }
  }

  private updateMinEndDate(): void {
    const startDate = this.startDateControl?.value;
    this.minEndDate = startDate ? new Date(startDate) : new Date(this.minStartDate);
  }

  get canByPassMinStartDate(): boolean {
    return this.currentLoggedinUser && (this.currentLoggedinUser.role.toLowerCase() == 'admin' || this.currentLoggedinUser.can_admin_billing == 'true');
  }
}
